import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
    FaArrowLeft, FaFileInvoiceDollar, FaExclamationTriangle, FaCalendarAlt,
    FaTruck, FaFileAlt, FaEnvelope, FaBalanceScale, FaPaperPlane, FaWallet,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import ErpTemplate from '../../../components/ErpTemplate';
import api from '../../../services/api';
import { moeda } from '../../../util/util';
import { OrigemMoeda } from '../../../components/ValorComOrigem';
import { FinField, FinInput } from '../../../components/FinField';
import { useSetFullBreadcrumbTrail } from '../../../context/BreadcrumbContext';
import ChecklistFaturamento from './ChecklistFaturamento';
import '../../../css/financeiro-parcela-pagar.css';

// Tela de PRÉ-ENVIO do faturamento — a conferência do OPERADOR DE EVENTO, antes
// de mandar o orçamento para a fila da Simone. Substituiu o modal de confirmação
// que existia na listagem de orçamentos (decisão do Rafael em 27/08/2026: o
// operador precisa VER o que vai para o Financeiro e dizer para quem o
// faturamento será enviado, não só clicar em "Enviar").
//
// Por isso a rota é /faturamentoEnvio/:orcamentoId, sem prefixo /financeiro —
// quem abre é Eventos. As telas do Financeiro (PainelFaturamento e
// DetalheFaturamento) leem o MESMO GET /orcamento/{id}/faturamento; aqui o
// recorte é o do operador: dados do evento, PO, fornecedores e destinatários.
//
// Ordem dos blocos: avisos -> KPIs -> evento -> PO -> fornecedores ->
// destinatários -> rodapé. Os avisos vêm primeiro de propósito: eles NÃO
// impedem o envio, servem para o operador saber o que o Financeiro vai
// encontrar faltando (mesma decisão do DetalheFaturamento).

const ROTULO_TIPO = { nd: 'ND', nf: 'NF', nao_informado: 'Não informado' };

// Mesma folga do backend (App\Classes\FaturamentoEmails): vírgula, ponto-e-vírgula
// ou quebra de linha separam; trim + lowercase; duplicado sai preservando a ordem
// (o primeiro digitado costuma ser o destinatário principal do cliente).
function normalizarEmails(texto) {
    var pedacos = String(texto || '').split(/[,;\r\n]+/);
    var saida = [];
    for (var i = 0; i < pedacos.length; i++) {
        var email = pedacos[i].trim().toLowerCase();
        if (email === '') continue;
        if (saida.indexOf(email) !== -1) continue;
        saida.push(email);
    }
    return saida;
}

// Espelha `FaturamentoEmails::invalidos()` — de propósito mais frouxo que o
// FILTER_VALIDATE_EMAIL do PHP: o front nunca pode barrar endereço que o backend
// aceitaria. Quem recusa de verdade (422) continua sendo o backend.
function emailsInvalidos(emails) {
    var formato = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/;
    return emails.filter(function (email) { return !formato.test(email); });
}

// "2026-08-27 14:34:41" -> "27/08/2026 14:34". Mesmo helper local do
// PainelFaturamento/DetalheFaturamento — lá também é local ao arquivo, então
// repete aqui em vez de mexer naquelas telas.
function dataHora(valor) {
    var texto = String(valor || '');
    if (texto.length < 10) return '—';
    var dia = texto.slice(8, 10) + '/' + texto.slice(5, 7) + '/' + texto.slice(0, 4);
    return texto.length >= 16 ? dia + ' ' + texto.slice(11, 16) : dia;
}

// Só a data, sem hora — "2026-08-27 00:00:00" -> "27/08/2026". Devolve '' (e não
// '—') para o chamador conseguir decidir o texto do campo vazio.
function dataBr(valor) {
    var texto = String(valor || '');
    if (texto.length < 10) return '';
    return texto.slice(8, 10) + '/' + texto.slice(5, 7) + '/' + texto.slice(0, 4);
}

// `evento.hora`/`horaFim` são coluna `time` ("14:30:00"), mas aceita datetime
// por segurança — banco antigo tem registro gravado dos dois jeitos.
function horaBr(valor) {
    var texto = String(valor || '');
    if (texto.length >= 16 && texto.indexOf(' ') > 0) return texto.slice(11, 16);
    return texto.length >= 5 ? texto.slice(0, 5) : '';
}

// Data + hora do evento, que no banco são duas colunas separadas.
function momento(data, hora) {
    var dia = dataBr(data);
    if (dia === '') return '';
    var h = horaBr(hora);
    return h === '' ? dia : dia + ' às ' + h;
}

// `moeda` devolve "1.234,56" sem o "R$" — o prefixo entra no JSX.
function reais(valor) {
    return 'R$ ' + moeda(valor);
}

function numero(valor) {
    var n = parseFloat(valor);
    return isNaN(n) ? 0 : n;
}

// { total, fornecedores } de uma faixa do resumo, tolerante a chave ausente.
function faixaResumo(resumo, chave) {
    var faixa = resumo && resumo[chave] ? resumo[chave] : null;
    return {
        total: faixa ? numero(faixa.total) : 0,
        fornecedores: faixa ? (parseInt(faixa.fornecedores, 10) || 0) : 0,
    };
}

function plural(qtd, singular, pluralizado) {
    return qtd + ' ' + (qtd === 1 ? singular : pluralizado);
}

// tipo nulo é "Não informado" — nunca ND. É pendência, não default.
function rotuloTipo(tipo) {
    return tipo && ROTULO_TIPO[tipo] ? ROTULO_TIPO[tipo] : ROTULO_TIPO.nao_informado;
}

// Lista de responsáveis ({id, nome}) em texto; `evento.responsavel` é CSV no banco.
function nomes(lista) {
    var pessoas = lista || [];
    var texto = pessoas.map(function (p) { return (p && p.nome) || ''; })
        .filter(Boolean)
        .join(', ');
    return texto;
}

export default function PreEnvioFaturamento() {
    const { orcamentoId } = useParams();
    const history = useHistory();
    const token = localStorage.getItem('token');
    const setFullTrail = useSetFullBreadcrumbTrail();

    const [dados, setDados] = useState(null);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState(null);
    const [emails, setEmails] = useState('');
    // O que está gravado no servidor, para saber se o campo tem alteração pendente.
    const [emailsSalvos, setEmailsSalvos] = useState('');
    const [salvandoEmails, setSalvandoEmails] = useState(false);
    const [enviando, setEnviando] = useState(false);

    useEffect(() => {
        setFullTrail([
            { path: '/orcamento', label: 'Orçamentos' },
            { path: '/faturamentoEnvio/' + orcamentoId, label: 'Enviar para Faturamento' },
        ]);
    }, [setFullTrail, orcamentoId]);

    const buscar = useCallback(() => {
        setCarregando(true);
        api({
            method: 'get',
            action: '/orcamento/' + orcamentoId + '/faturamento',
            token: token,
        })
            .then(function (response) {
                const data = response.data || {};
                if (data.status === false) {
                    setDados(null);
                    setErro(data.error || 'Não foi possível carregar o faturamento.');
                    return;
                }
                setDados(data);
                setErro(null);
                // O campo nasce com o que já foi informado num envio anterior; o
                // operador edita por cima em vez de digitar tudo de novo.
                setEmails((data.emails_faturamento || []).join(', '));
                setEmailsSalvos((data.emails_faturamento || []).join(', '));
            })
            .catch(function (err) {
                const d = (err && err.response && err.response.data) || {};
                setDados(null);
                setErro(d.error || d.mensagem || 'Erro ao carregar o faturamento.');
                toast.error('Erro ao carregar o faturamento.');
            })
            .finally(function () {
                setCarregando(false);
            });
    }, [orcamentoId, token]);

    useEffect(() => {
        buscar();
    }, [buscar]);

    const evento = (dados && dados.evento) || {};
    const datas = evento.datas || {};
    const po = (dados && dados.po) || {};
    const resumo = (dados && dados.resumo) || {};
    const fornecedores = (dados && dados.fornecedores) || [];
    // D-0056: o checklist da API decide se pode enviar (a API recusa com 422 do mesmo jeito).
    const checklist = (dados && dados.checklist) || null;
    const jaEnviado = !!(dados && dados.orcamento && dados.orcamento.enviado_em);
    const checklistVerde = !checklist || !!checklist.pode_enviar;

    // Adiantamento recebido x em aberto. Esta tela não mostrava adiantamento
    // nenhum, e é justamente a do operador que MANDA o faturamento — o erro de
    // 27/08/2026 (dizer ao cliente que o evento tinha adiantamento sem o
    // dinheiro ter caído) nasce aqui, antes de chegar na Simone.
    const adiantamento = (dados && dados.adiantamento) || {};
    const composicaoAdiantamento = adiantamento.composicao || null;
    const adiantamentoRecebido = numero(adiantamento.recebido);
    const adiantamentoEmAberto = numero(adiantamento.em_aberto);

    const resumoNd = faixaResumo(resumo, 'nd');
    const resumoNf = faixaResumo(resumo, 'nf');
    const resumoNaoInformado = faixaResumo(resumo, 'nao_informado');
    const totalGeral = numero(resumo.total_geral);

    const listaEmails = normalizarEmails(emails);
    const invalidos = emailsInvalidos(listaEmails);
    const emailsAlterados = listaEmails.join(', ') !== emailsSalvos;

    // Volta sempre para a listagem de orçamentos do evento — é de lá que a tela
    // é aberta. O id do evento vem do payload, não da URL (a rota carrega só o
    // orçamento), então antes de carregar cai na listagem geral.
    const voltar = useCallback(() => {
        history.push('/orcamento' + (evento.id ? '?idEvento=' + evento.id : ''));
    }, [history, evento.id]);

    // Salvar sem enviar. Antes, o destinatário só persistia junto com o "Enviar
    // para Faturamento" — quem digitava e saía da tela perdia o que escreveu,
    // sem nenhum aviso. O PUT é o mesmo: ele é idempotente no carimbo de envio e
    // aceita atualizar só os e-mails.
    const salvarEmails = () => {
        if (salvandoEmails || invalidos.length > 0) { return; }
        setSalvandoEmails(true);
        api({
            method: 'put',
            action: '/orcamento/' + orcamentoId + '/enviarFaturamento',
            token: token,
            data: { emails: listaEmails },
        })
            .then(function () {
                toast.success('E-mails salvos.');
                setEmailsSalvos(listaEmails.join(', '));
            })
            .catch(function (err) {
                const d = (err && err.response && err.response.data) || {};
                toast.error(d.error || d.mensagem || 'Erro ao salvar os e-mails.');
            })
            .finally(function () {
                setSalvandoEmails(false);
            });
    };

    // D-0056 (Rafael 03/11): só envia com o checklist verde — desfaz a decisão de 27/08, em que a
    // pendência só avisava. `enviar: true` é o que carimba o envio; sem ele o PUT só grava os e-mails.
    const enviar = () => {
        if (!dados || enviando || invalidos.length > 0 || !checklistVerde) { return; }
        setEnviando(true);
        api({
            method: 'put',
            action: '/orcamento/' + orcamentoId + '/enviarFaturamento',
            token: token,
            data: { emails: listaEmails, enviar: true },
        })
            .then(function () {
                toast.success('Enviado para Faturamento.');
                voltar();
            })
            .catch(function (err) {
                const d = (err && err.response && err.response.data) || {};
                toast.error(d.error || d.mensagem || 'Erro ao enviar para Faturamento.');
                if (d.checklist) {
                    setDados(function (atual) { return atual ? Object.assign({}, atual, { checklist: d.checklist }) : atual; });
                }
            })
            .finally(function () {
                setEnviando(false);
            });
    };

    // Justificativa de pago ≠ orçado (D-0056): a API grava com os valores de agora e devolve o checklist.
    const justificar = (fornecedorId, texto) => {
        return api({
            method: 'put',
            action: '/orcamento/' + orcamentoId + '/faturamentoJustificativa',
            token: token,
            data: { fornecedor_id: fornecedorId, justificativa: texto },
        })
            .then(function (response) {
                const d = response.data || {};
                toast.success('Justificativa salva.');
                if (d.checklist) {
                    setDados(function (atual) { return atual ? Object.assign({}, atual, { checklist: d.checklist }) : atual; });
                }
                return true;
            })
            .catch(function (err) {
                const d = (err && err.response && err.response.data) || {};
                toast.error(d.error || d.mensagem || 'Erro ao salvar a justificativa.');
                return false;
            });
    };

    const subtitulo = dados
        ? [
            evento.nome || 'Evento sem nome',
            evento.codigo ? '#' + evento.codigo : '',
            dados.orcamento.titulo || 'Orçamento sem título',
            dados.orcamento.enviado_em ? 'já enviado em ' + dataHora(dados.orcamento.enviado_em) : '',
        ].filter(Boolean).join(' · ')
        : 'Confira o que vai para o Financeiro antes de enviar.';

    // Datas: das nove que o backend devolve, a tela mostra as quatro que o
    // operador confere neste momento — realização (início/fim), fechamento e a
    // previsão de faturamento. `replanejadoInicio`/`replanejadoFim` aparecem
    // como nota embaixo da data original só quando o evento foi remarcado.
    const inicio = momento(datas.data, datas.hora);
    const fim = momento(datas.dataFim, datas.horaFim);
    const replanejadoInicio = dataBr(datas.replanejadoInicio);
    const replanejadoFim = dataBr(datas.replanejadoFim);
    const fechamento = dataBr(datas.dataFechamento);
    const previsao = dataBr(datas.previsaoFaturamento);

    const responsaveis = nomes(evento.responsavel);
    const respCliente = (evento.responsavelcliente && evento.responsavelcliente.nome) || '';
    const diretor = (evento.diretorresponsavel && evento.diretorresponsavel.nome) || '';

    // Linha rótulo/valor das tabelas kv — o visual do rótulo vem do modificador
    // .fin-pp__tbl--kv, sem estilo inline repetido.
    const Linha = ({ label, children }) => (
        <tr>
            <td>{label}</td>
            <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{children}</td>
        </tr>
    );

    const vazio = <span className="fin-pp__cell-muted">Não informado</span>;

    return (
        <ErpTemplate>
            <div className="main-content container-fluid fin-pp">

                {/* Header */}
                <div className="fin-pp__head">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                        <button type="button" className="fin-pp__btn fin-pp__btn--sm"
                            onClick={voltar} title="Voltar para os orçamentos do evento">
                            <FaArrowLeft size={13} />
                        </button>
                        <div style={{ minWidth: 0 }}>
                            <h1 className="fin-pp__title">Enviar para Faturamento</h1>
                            <p className="fin-pp__subtitle">{subtitulo}</p>
                        </div>
                    </div>
                </div>

                {carregando && !dados && (
                    <div className="fin-pp__card" style={{ marginBottom: 18 }}>
                        <div className="fin-pp__empty" style={{ padding: '48px 0' }}>Carregando…</div>
                    </div>
                )}

                {!carregando && erro && (
                    <div className="fin-pp__card" style={{ marginBottom: 18 }}>
                        <div className="fin-pp__empty" style={{ padding: '40px 16px' }}>
                            <FaExclamationTriangle size={26} style={{ color: 'var(--fin-wn)' }} />
                            <div style={{ marginTop: 8 }}>{erro}</div>
                        </div>
                    </div>
                )}

                {dados && (<>
                    {/* ── 1. Checklist (D-0056, Rafael 03/11) ─────────────────
                        Substitui os "Avisos" de 27/08: agora o envio só libera com tudo
                        o que bloqueia em verde, e cada item diz onde resolver. */}
                    <ChecklistFaturamento checklist={checklist} editavel={!jaEnviado} onJustificar={justificar} />

                    {/* ── Prazo de faturamento (Megui, 04/09) ──────────────────
                        Cada cliente tem um prazo, contado do fim do evento, para a
                        nota ser emitida. Passou disso, o cliente não aceita mais —
                        e hoje ninguém via essa data em lugar nenhum. */}
                    {(() => {
                        const cob = (dados && dados.cobranca) || {};
                        if (!cob.data_limite_faturamento) return null;
                        const dias = cob.dias_para_faturar;
                        const venceu = typeof dias === 'number' && dias < 0;
                        const perto  = typeof dias === 'number' && dias >= 0 && dias <= 7;
                        const dataBr = new Date(cob.data_limite_faturamento + 'T12:00:00').toLocaleDateString('pt-BR');
                        return (
                            <div
                                className="d-flex align-items-center gap-2"
                                style={{
                                    marginBottom: 18,
                                    padding: '10px 14px',
                                    borderRadius: 8,
                                    border: '1px solid ' + (venceu ? '#f5b5b5' : perto ? '#f0c36d' : 'var(--fin-acb)'),
                                    background: venceu ? '#fdf1f1' : perto ? '#fff8e8' : 'var(--fin-acw)',
                                    fontSize: '0.88rem',
                                }}>
                                <span style={{ fontSize: '1.1rem', lineHeight: 1 }}>{venceu ? '⚠️' : '🗓️'}</span>
                                <span>
                                    <strong>
                                        {venceu
                                            ? 'Prazo de faturamento vencido em ' + dataBr
                                            : 'Faturar até ' + dataBr}
                                    </strong>
                                    <span className="text-muted">
                                        {' — '}prazo de {cob.prazo_faturamento_dias} dias do cliente, contado do fim do evento
                                        {typeof dias === 'number' && !venceu ? ' (faltam ' + dias + ' dia' + (dias === 1 ? '' : 's') + ')' : ''}
                                        {venceu ? ' (há ' + Math.abs(dias) + ' dia' + (Math.abs(dias) === 1 ? '' : 's') + ')' : ''}
                                    </span>
                                </span>
                            </div>
                        );
                    })()}

                    {/* ── 2. Resumo ND x NF ────────────────────────────────────
                        `.fin-pp__kpis` não tem margem própria: sem o marginBottom
                        a primeira região cola no card do KPI. */}
                    <section className="fin-pp__kpis" style={{ marginBottom: 18 }}>
                        <div className="fin-pp__kpi">
                            <div className="fin-pp__kpi-top">
                                <span className="fin-pp__kpi-label">ND</span>
                                <span className="fin-pp__kpi-ic"><FaFileInvoiceDollar size={16} /></span>
                            </div>
                            <div className="fin-pp__kpi-value">{reais(resumoNd.total)}</div>
                            <OrigemMoeda origem={resumo.nd && resumo.nd.origem_moeda} />
                            <div className="fin-pp__kpi-sub">{plural(resumoNd.fornecedores, 'fornecedor', 'fornecedores')}</div>
                        </div>

                        <div className="fin-pp__kpi">
                            <div className="fin-pp__kpi-top">
                                <span className="fin-pp__kpi-label">NF</span>
                                <span className="fin-pp__kpi-ic"><FaFileInvoiceDollar size={16} /></span>
                            </div>
                            <div className="fin-pp__kpi-value">{reais(resumoNf.total)}</div>
                            <OrigemMoeda origem={resumo.nf && resumo.nf.origem_moeda} />
                            <div className="fin-pp__kpi-sub">{plural(resumoNf.fornecedores, 'fornecedor', 'fornecedores')}</div>
                        </div>

                        <div className="fin-pp__kpi">
                            <div className="fin-pp__kpi-top">
                                <span className="fin-pp__kpi-label">Não informado</span>
                                <span className="fin-pp__kpi-ic"><FaExclamationTriangle size={16} /></span>
                            </div>
                            <div className={'fin-pp__kpi-value' + (resumoNaoInformado.total > 0 ? ' is-warn' : '')}>
                                {reais(resumoNaoInformado.total)}
                            </div>
                            <OrigemMoeda origem={resumo.nao_informado && resumo.nao_informado.origem_moeda} />
                            <div className="fin-pp__kpi-sub">
                                {resumoNaoInformado.total > 0
                                    ? plural(resumoNaoInformado.fornecedores, 'fornecedor', 'fornecedores') + ' sem ND/NF no Efetivo'
                                    : 'Todos os fornecedores classificados'}
                            </div>
                        </div>

                        <div className="fin-pp__kpi">
                            <div className="fin-pp__kpi-top">
                                <span className="fin-pp__kpi-label">Total geral</span>
                                <span className="fin-pp__kpi-ic"><FaBalanceScale size={16} /></span>
                            </div>
                            <div className="fin-pp__kpi-value">{reais(totalGeral)}</div>
                            <OrigemMoeda origem={resumo.origem_moeda} />
                            <div className="fin-pp__kpi-sub">Orçado vigente dos fornecedores do evento</div>
                        </div>

                        {/* O número grande é o RECEBIDO — o que o cliente já pagou.
                            `composicao.recebido` NÃO entra aqui: aquilo é recebimento
                            de ND/NF, outro dinheiro. */}
                        <div className="fin-pp__kpi">
                            <div className="fin-pp__kpi-top">
                                <span className="fin-pp__kpi-label">Adiantamento recebido</span>
                                <span className="fin-pp__kpi-ic"><FaWallet size={16} /></span>
                            </div>
                            <div className={'fin-pp__kpi-value' + (adiantamentoEmAberto > 0 ? ' is-warn' : '')}>
                                {reais(adiantamentoRecebido)}
                            </div>
                            <div className="fin-pp__kpi-sub">
                                {'Disponível ' + reais(adiantamento.disponivel)}
                                {composicaoAdiantamento ? ' · usado ' + reais(composicaoAdiantamento.usado_fornecedores) : ''}
                            </div>
                            {adiantamentoEmAberto > 0 && (
                                <div className="fin-pp__kpi-sub" style={{ marginTop: 6 }}>
                                    <span className="fin-pp__minitag is-warn">
                                        Em aberto {reais(adiantamentoEmAberto)}
                                    </span>
                                    <span style={{ marginLeft: 6 }}>ainda não recebido — não cite no faturamento</span>
                                </div>
                            )}
                        </div>
                    </section>

                    {/* ── 3. Dados do evento ─────────────────────────────────── */}
                    <section className="fin-pp__region" style={{ marginBottom: 18 }}>
                        <div className="fin-pp__cphead">
                            <span className="fin-pp__cphead-ic"><FaCalendarAlt size={16} /></span>
                            <div style={{ minWidth: 0, flex: 1 }}>
                                <h2 className="fin-pp__cphead-title">Dados do evento</h2>
                                <p className="fin-pp__cphead-sub">
                                    O que o Financeiro usa para identificar o evento na nota. Errado aqui,
                                    errado na nota — corrija no cadastro do evento antes de enviar.
                                </p>
                            </div>
                        </div>

                        <div className="fin-pp__region-body">
                            <div className="fin-pp__card" style={{ overflow: 'hidden' }}>
                                <div className="fin-pp__tblwrap">
                                    <table className="fin-pp__tbl fin-pp__tbl--kv">
                                        <tbody>
                                            <Linha label="Endereço">
                                                {evento.endereco ? evento.endereco : vazio}
                                                {evento.enderecocep ? (
                                                    <div className="fin-pp__cell-muted" style={{ marginTop: 4 }}>
                                                        CEP {evento.enderecocep}
                                                    </div>
                                                ) : null}
                                            </Linha>
                                            <Linha label="Local">
                                                {evento.localNaoDefinido
                                                    ? <span className="fin-pp__minitag is-warn">Local ainda não definido</span>
                                                    : (evento.local ? evento.local : vazio)}
                                            </Linha>
                                            <Linha label="Início">
                                                {inicio === '' ? vazio : inicio}
                                                {replanejadoInicio !== '' ? (
                                                    <div className="fin-pp__cell-muted" style={{ marginTop: 4 }}>
                                                        replanejado para {replanejadoInicio}
                                                    </div>
                                                ) : null}
                                            </Linha>
                                            <Linha label="Fim">
                                                {fim === '' ? vazio : fim}
                                                {replanejadoFim !== '' ? (
                                                    <div className="fin-pp__cell-muted" style={{ marginTop: 4 }}>
                                                        replanejado para {replanejadoFim}
                                                    </div>
                                                ) : null}
                                            </Linha>
                                            <Linha label="Fechamento">
                                                {fechamento === '' ? vazio : fechamento}
                                            </Linha>
                                            <Linha label="Previsão de faturamento">
                                                {previsao === '' ? vazio : previsao}
                                            </Linha>
                                            <Linha label="Responsável Access">
                                                {responsaveis === '' ? vazio : responsaveis}
                                            </Linha>
                                            <Linha label="Responsável do cliente">
                                                {respCliente === '' ? vazio : respCliente}
                                            </Linha>
                                            <Linha label="Diretor responsável">
                                                {diretor === '' ? vazio : diretor}
                                            </Linha>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* ── 4. PO ───────────────────────────────────────────────── */}
                    <section className="fin-pp__region" style={{ marginBottom: 18 }}>
                        <div className="fin-pp__cphead">
                            <span className="fin-pp__cphead-ic"><FaFileAlt size={16} /></span>
                            <div style={{ minWidth: 0, flex: 1 }}>
                                <h2 className="fin-pp__cphead-title">PO</h2>
                                <p className="fin-pp__cphead-sub">
                                    Número e arquivo da ordem de compra do cliente, como estão no cadastro do evento.
                                </p>
                            </div>
                            {po.nao_aplicavel ? (
                                <span className="fin-pp__cphead-pill">Não se aplica</span>
                            ) : null}
                        </div>

                        <div className="fin-pp__region-body">
                            {po.nao_aplicavel ? (
                                <div className="fin-pp__card" style={{ padding: '16px 18px' }}>
                                    <div className="fin-pp__notice" style={{ margin: 0 }}>
                                        <span className="fin-pp__pill is-info" style={{ flex: 'none' }}>Sem PO</span>
                                        <span style={{ flex: 1, minWidth: 0 }}>
                                            <strong>Este evento não tem PO.</strong> O cadastro do evento está
                                            marcado como "PO não aplicável" — o Financeiro não vai cobrar o número.
                                        </span>
                                    </div>
                                </div>
                            ) : (
                                <div className="fin-pp__card" style={{ overflow: 'hidden' }}>
                                    <div className="fin-pp__tblwrap">
                                        <table className="fin-pp__tbl fin-pp__tbl--kv">
                                            <tbody>
                                                <Linha label="Número">
                                                    {po.numero ? <strong>{po.numero}</strong> : (
                                                        <>
                                                            <span className="fin-pp__pill is-danger">PO faltando</span>
                                                            <div className="fin-pp__cell-muted" style={{ marginTop: 6 }}>
                                                                O evento exige PO e o número não foi preenchido —
                                                                informe no cadastro do evento.
                                                            </div>
                                                        </>
                                                    )}
                                                </Linha>
                                                <Linha label="Arquivo">
                                                    {po.arquivo ? (
                                                        <a
                                                            href={process.env.REACT_APP_API_URL + '/arquivo/download/' + po.arquivo}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            style={{ color: 'var(--fin-ac)', textDecoration: 'underline' }}
                                                        >
                                                            Abrir arquivo da PO
                                                        </a>
                                                    ) : (
                                                        <span className="fin-pp__minitag is-warn">Nenhum arquivo anexado</span>
                                                    )}
                                                </Linha>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                    </section>

                    {/* ── 5. Fornecedores ─────────────────────────────────────── */}
                    <section className="fin-pp__region" style={{ marginBottom: 18 }}>
                        <div className="fin-pp__cphead">
                            <span className="fin-pp__cphead-ic"><FaTruck size={16} /></span>
                            <div style={{ minWidth: 0, flex: 1 }}>
                                <h2 className="fin-pp__cphead-title">Fornecedores</h2>
                                <p className="fin-pp__cphead-sub">
                                    ND/NF vem do Efetivo. Orçado é o valor vigente (Fechamento → Negociado → Pré-Evento).
                                </p>
                            </div>
                            <span className="fin-pp__cphead-pill">
                                {plural(fornecedores.length, 'fornecedor', 'fornecedores')}
                            </span>
                        </div>

                        <div className="fin-pp__region-body">
                            <div className="fin-pp__card" style={{ overflow: 'hidden' }}>
                                <div className="fin-pp__tblwrap">
                                    <table className="fin-pp__tbl fin-pp__tbl--wide">
                                        <thead>
                                            <tr>
                                                <th>Fornecedor</th>
                                                <th style={{ width: 170 }}>Tipo</th>
                                                <th style={{ width: 150 }}>Orçado</th>
                                                <th style={{ width: 170 }}>Pago</th>
                                                <th style={{ width: 300 }}>Notas</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {fornecedores.length === 0 && (
                                                <tr>
                                                    <td colSpan={5} className="fin-pp__empty">
                                                        Nenhum fornecedor neste orçamento.
                                                    </td>
                                                </tr>
                                            )}
                                            {fornecedores.map(function (f) {
                                                var notas = f.nfs || [];
                                                return (
                                                    <tr key={f.fornecedor_id}>
                                                        <td className="fin-pp__cell-strong">{f.fornecedor_nome || '—'}</td>
                                                        <td>
                                                            <span className={'fin-pp__minitag ' + (f.tipo ? 'is-info' : 'is-warn')}>
                                                                {rotuloTipo(f.tipo)}
                                                            </span>
                                                        </td>
                                                        <td className="fin-pp__cell-num">
                                                            {reais(f.valor_orcado)}
                                                            <OrigemMoeda origem={f.origem_moeda} />
                                                        </td>
                                                        <td className="fin-pp__cell-num">
                                                            {reais(f.valor_pago)}
                                                            {!f.pagamento_realizado && (
                                                                <div style={{ marginTop: 4 }}>
                                                                    <span className="fin-pp__minitag is-warn">sem pagamento</span>
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {notas.length === 0 ? (
                                                                <span className="fin-pp__cell-muted">
                                                                    {f.tipo === 'nf' ? 'Nenhuma nota anexada' : '—'}
                                                                </span>
                                                            ) : (
                                                                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                                                    {notas.map(function (n) {
                                                                        return (
                                                                            <span key={n.id} className="fin-pp__minitag">
                                                                                NF {n.numero || 's/nº'}
                                                                                {n.status ? ' · ' + n.status : ''}
                                                                            </span>
                                                                        );
                                                                    })}
                                                                </div>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* ── 6. Destinatários do e-mail ──────────────────────────── */}
                    <section className="fin-pp__region" style={{ marginBottom: 18 }}>
                        <div className="fin-pp__cphead">
                            <span className="fin-pp__cphead-ic"><FaEnvelope size={16} /></span>
                            <div style={{ minWidth: 0, flex: 1 }}>
                                <h2 className="fin-pp__cphead-title">Destinatários do e-mail</h2>
                                <p className="fin-pp__cphead-sub">
                                    Para quem o faturamento deste evento deve ser enviado no cliente.
                                </p>
                            </div>
                            {listaEmails.length > 0 ? (
                                <span className="fin-pp__cphead-pill">
                                    {plural(listaEmails.length, 'endereço', 'endereços')}
                                </span>
                            ) : null}
                        </div>

                        <div className="fin-pp__region-body">
                            <div className="fin-pp__card" style={{ padding: '16px 18px' }}>
                                <div className="fin-pp__filters">
                                    <FinField label="E-mails" htmlFor="emailsFaturamento" full>
                                        <FinInput
                                            id="emailsFaturamento"
                                            type="text"
                                            placeholder="fulano@cliente.com, financeiro@cliente.com"
                                            value={emails}
                                            onChange={function (e) { setEmails(e.target.value); }}
                                        />
                                    </FinField>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10, flexWrap: 'wrap' }}>
                                    <button
                                        type="button"
                                        className="fin-pp__btn fin-pp__btn--sm"
                                        disabled={salvandoEmails || invalidos.length > 0 || !emailsAlterados}
                                        onClick={salvarEmails}
                                    >
                                        {salvandoEmails ? 'Salvando…' : 'Salvar e-mails'}
                                    </button>
                                    {emailsAlterados && invalidos.length === 0 && (
                                        <span className="fin-pp__minitag is-warn">Não salvo</span>
                                    )}
                                    {!emailsAlterados && listaEmails.length > 0 && (
                                        <span className="fin-pp__minitag is-ok">Salvo</span>
                                    )}
                                </div>
                                <p className="fin-pp__cell-muted" style={{ margin: '10px 0 0' }}>
                                    Separe por vírgula para informar mais de um. A Simone vai usar estes
                                    endereços para enviar o faturamento ao cliente. Salvar aqui guarda o
                                    destinatário sem enviar o orçamento — dá para voltar depois.
                                </p>
                            </div>
                        </div>
                    </section>

                    {/* ── 7. Rodapé ───────────────────────────────────────────── */}
                    <div className="fin-pp__card" style={{ padding: '16px 18px', marginBottom: 24 }}>
                        {/* Aviso acima do botão + botão desabilitado: a mesma conta que
                            o backend faz antes de devolver 422 no e-mail inválido. */}
                        {invalidos.length > 0 && (
                            <div className="fin-pp__notice" style={{ margin: '0 0 12px' }}>
                                <span className="fin-pp__pill is-danger" style={{ flex: 'none' }}>Corrija</span>
                                <span style={{ flex: 1, minWidth: 0 }}>
                                    {invalidos.length === 1
                                        ? 'E-mail inválido: ' + invalidos[0] + '.'
                                        : 'E-mails inválidos: ' + invalidos.join(', ') + '.'}
                                </span>
                            </div>
                        )}
                        {invalidos.length === 0 && listaEmails.length === 0 && (
                            <div className="fin-pp__notice" style={{ margin: '0 0 12px' }}>
                                <span className="fin-pp__pill is-warn" style={{ flex: 'none' }}>Sem destinatário</span>
                                <span style={{ flex: 1, minWidth: 0 }}>
                                    Nenhum e-mail informado. Dá para enviar assim mesmo, mas a Simone vai
                                    precisar procurar o endereço com você.
                                </span>
                            </div>
                        )}

                        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                            <button type="button" className="fin-pp__btn" onClick={voltar} disabled={enviando}>
                                Cancelar
                            </button>
                            <button type="button" className="fin-pp__btn fin-pp__btn--primary"
                                onClick={enviar} disabled={enviando || invalidos.length > 0 || !checklistVerde}
                                title={!checklistVerde
                                    ? 'Resolva os itens em vermelho do checklist antes de enviar.'
                                    : invalidos.length > 0
                                        ? 'Corrija os e-mails antes de enviar.'
                                        : 'Enviar este orçamento para a fila do Faturamento.'}>
                                <FaPaperPlane size={13} /> {enviando ? 'Enviando…' : 'Enviar para Faturamento'}
                            </button>
                        </div>
                    </div>
                </>)}

            </div>
        </ErpTemplate>
    );
}
